import React, {
	useEffect,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import MomentCore from 'moment';

import {
	makeStyles,
	Grid,
	RootRef,
	Tooltip
} from '@material-ui/core';

const lineColor = "#dbdada";
const styles = makeStyles(theme => ({
	container: {
		position: "relative"
	},

	line: {
		height: theme.spacing(0.25),
		backgroundColor: lineColor
	},

	openEndedLine: {
		backgroundImage: `linear-gradient(to right, ${lineColor}, 95%, ${theme.palette.common.white})`
	},

	event: {
		position: "absolute",
		bottom: theme.spacing(-0.625)
	},

	label: {
		marginBottom: theme.spacing(2)
	},

	connector: {
		position: "absolute",
		width: theme.spacing(0.25),
		height: theme.spacing(1.25),
		backgroundColor: lineColor,
		left: theme.spacing(0.625),
		top: theme.spacing(2.125)
	},

	lastConnector: {
		left: theme.spacing(4)
	},

	dot: {
		borderRadius: "50%",
		width: theme.spacing(1.5),
		height: theme.spacing(1.5)
	},

	tooltip: {
		maxWidth: 200
	}
}));

function HorizontalTimeline(props) {
	const lineRef = useRef();
	const [adjustedEvents, setAdjustedEvents] = useState();

	let classes = styles(),
		{events, isOpenEnded, labelColor, tooltip} = props;

	useEffect(() => {
		if (events && events.length) {
			let unit = "days",
				start = new MomentCore(events[0].date),
				end = new MomentCore(events[events.length-1].date),
				delta = end.diff(start, unit) + 1; // +1 to include day

			if (delta < 5 && delta > 1) {
				unit = "hours";
				delta = end.diff(start, unit) + 1; // +1 to include hour
			}
			else if (delta <= 1) {
				unit = "minutes";
				delta = end.diff(start, unit) + 1; // +1 to include minute
			}

			// minus 12 px from total width; fixes issues with certain dots going past the end of the line
			let lineLength = lineRef && lineRef.current ? lineRef.current.offsetWidth - 12 : 0,
				pxPerUnit = (lineLength / delta) - (isOpenEnded ? 5 : 0);

			events.map((event, idx) => event.left = idx === 0 ? 0 : ((new MomentCore(event.date)).diff(start, unit)+1) * pxPerUnit);
			setAdjustedEvents(events);
		}

	}, [events, setAdjustedEvents, isOpenEnded]);

	return (
		<Tooltip
			placement="top"
			title={tooltip}
			disableFocusListener
			classes={{tooltip: classes.tooltip}}>
			<Grid container
				direction="column"
				justify="flex-end"
				alignItems="stretch"
				className={classes.container}>
				<RootRef rootRef={lineRef}>
					<Grid item className={`${classes.line} ${isOpenEnded ? classes.openEndedLine : ""}`}></Grid>
				</RootRef>
				{adjustedEvents && adjustedEvents.map((event, idx) => {
					let isLastEvent = !isOpenEnded && idx === adjustedEvents.length-1;
					let isFirstEvent = idx == 0;
					
					// if middle label is too close to start or end, hide it
					if (event.label && !isFirstEvent && !isLastEvent) {
						if (event.left < 40 || event.left > (lineRef.current.offsetWidth - 80) && !isOpenEnded) {
							event.label = null;
						}
					}

					return (
						<Grid item
							key={idx}
							className={classes.event}
							style={isLastEvent ? {right: 0} : {left: event.left}}>
							<Grid container
								direction="column"
								alignItems={isLastEvent ? "flex-end" : null}>
								<Grid item className={classes.label} style={{color: labelColor}}>{event.label}</Grid>
								{event.label ?
									<Grid item className={`${classes.connector} ${isLastEvent ? classes.lastConnector : ""}`}></Grid> : null
								}
								<Grid item className={classes.dot} style={event.style}></Grid>
							</Grid>
						</Grid>
					);
				})}
			</Grid>
		</Tooltip>
	);
}

HorizontalTimeline.defaultProps = {
	isOpenEnded: false
};

HorizontalTimeline.propTypes = {
	events: PropTypes.array,
	isOpenEnded: PropTypes.bool,
	labelColor: PropTypes.string,
	tooltip: PropTypes.any
};

export default HorizontalTimeline;